import type { StateCreator } from 'zustand';
import { fxKey } from '../../lib/fx';
import type { ItineraryState } from '../itinerary-store';

/**
 * FX rate cache — phase 3c of cost tracking (ADR-0059).
 * Persistent forever because past-date rates are immutable
 * (Frankfurter publishes daily after the ECB fixing). Today's
 * rate becomes a past-date rate the moment the calendar day
 * rolls over, so no TTL bookkeeping is needed — the natural
 * key transition handles staleness.
 *
 * Reads go through `useFxRates`, which schedules a background
 * fetch on cache miss and writes back via `setFxRate`. Failed
 * fetches don't write — the next render retries.
 */
export interface FxSlice {
  /** Keyed by `${YYYY-MM-DD}_${FROM}_${TO}`. Value is the rate
   *  multiplier — e.g. `fxRates['2026-05-15_USD_EUR'] = 0.92`
   *  means 1 USD = 0.92 EUR on that date. */
  fxRates: Record<string, number>;
  setFxRate: (date: string, from: string, to: string, rate: number) => void;
}

export const createFxSlice: StateCreator<ItineraryState, [], [], FxSlice> = (set) => ({
  fxRates: {},
  setFxRate: (date, from, to, rate) => {
    if (!Number.isFinite(rate) || rate <= 0) return;
    set((s) => ({
      fxRates: { ...s.fxRates, [fxKey(date, from, to)]: rate },
    }));
  },
});
